import { useCallback } from 'react'

import { ColumnAdapter, TableAdapter } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { CloseCircle } from '@makyoapp/makyo-ui'

import { ActionDataType, ActionInputdata } from '@/shared/action.types'
import { appIconKeys } from '@/shared/icons'
import { Option } from '@/shared/types'

import { RenderColumn } from '../RenderColumn'

type RenderSelectedColumnProps = {
  dataType: ActionDataType
  onClear(): void
  value: string
  selectedColumn?: ColumnAdapter
  inputData?: ActionInputdata
  options?: Option[]
  tables?: TableAdapter[]
}

const iconMap: any = {
  [ActionDataType.Column]: <MaterialIcon icon={appIconKeys.tableColumn} size={14} />,
  [ActionDataType.ProfileData]: <MaterialIcon icon='person' size={14} />,
  [ActionDataType.SingleCell]: <MaterialIcon icon={appIconKeys.columns['single-cell']} size={14} />,
  [ActionDataType.DateTime]: <MaterialIcon icon='event' size={14} />,
  [ActionDataType.ClearValue]: <MaterialIcon icon='change_circle' size={14} />,
  [ActionDataType.SessionID]: <MaterialIcon icon={appIconKeys.tableSession} size={14} />,
  [ActionDataType.Comment]: <MaterialIcon icon='comment' size={14} />,
  [ActionDataType.ActionOutput]: <MaterialIcon icon={appIconKeys.actionOutput} size={14} />,
  [ActionDataType.UniqueID]: <MaterialIcon icon={appIconKeys.uniqueId} size={14} />,
  [ActionDataType.NewsletterEmail]: <MaterialIcon icon={appIconKeys.newsletterEmail} size={14} />,
}

const commentLabelMap: any = {
  comment: 'Comment',
  'parent-id': 'Comment Parent ID',
}

export function RenderSelectedColumn({
  dataType,
  value,
  selectedColumn,
  onClear,
  inputData,
  options,
  tables,
}: RenderSelectedColumnProps) {
  const variableIcon = iconMap[dataType] || null

  const getLabelContent = useCallback(() => {
    switch (dataType) {
      case ActionDataType.Comment:
        return commentLabelMap[value]
      case ActionDataType.SessionID:
        return 'Session ID'
      case ActionDataType.DateTime:
        return 'Date & Time'
      case ActionDataType.ClearValue:
        return 'Clear Value'
      case ActionDataType.ActionOutput:
        if (inputData && options) {
          const selectedAction = options.find((option: any) => option.id === inputData.value)
          if (selectedAction) {
            if (inputData.tableId && inputData.colId) {
              const table = tables?.find((t) => t.id === inputData.tableId)
              const column = table?.columns?.find((c) => c.id === inputData.colId)
              return `Node Output -> ${selectedAction.label} -> ${column?.name || inputData.colId}`
            }
            return `Node Output -> ${selectedAction.label}`
          }
        }
        return 'Node Output'
      case ActionDataType.UniqueID:
        return 'Unique Identifier'
      case ActionDataType.NewsletterEmail:
        return 'Newsletter Email'
      default:
        const getTableName = () => {
          if (!selectedColumn || !tables) return 'Unknown Table'

          const table = tables.find((t) => t.columns?.some((col) => col.id === selectedColumn.id))

          return table?.name || 'Unknown Table'
        }
        return (
          <RenderColumn
            withStyle={false}
            col={selectedColumn}
            width={12}
            height={12}
            showDetailedInfo={true}
            tableName={getTableName()}
          />
        )
    }
  }, [dataType, value, inputData, options, selectedColumn, tables])

  const renderLabel = () => {
    return (
      <div className='flex items-center border bdr rounded-sm overflow-hidden mk-bg hover:bg-zinc-50 dark:hover:bg-zinc-700'>
        <div className='py-1 px-1.5 border-r bdr flex items-center justify-center'>{variableIcon}</div>
        <span className='px-2'>{getLabelContent()}</span>
      </div>
    )
  }

  return (
    <div className='bg-zinc-200 dark:bg-slate-900 h-9 py-2 px-3 pr-8 rounded-sm flex items-center justify-between'>
      {renderLabel()}
      <button type='button' className='flex items-center justify-center' onClick={onClear}>
        <CloseCircle />
      </button>
    </div>
  )
}
